/**
 * Sync Catalog API Contract (SCH-2681, KTD16)
 *
 * GET /internal/platform-data-sync/sync-catalog — `INTERNAL_API_KEY`; read by
 *   `pnpm sj` and the platform-sync skill.
 * GET /v1/schools/:schoolId/sync-catalog — Clerk session, school admin of that
 *   school or super-admin (gated like `/v1/schools/:id/data-sync-configs`); read
 *   by the web platform-config page, which cannot send the internal key.
 *
 * Both routes call one use case and return this payload: every registered sync
 * type with the fixed facts its descriptor declares, and every named set in run
 * order. The payload is the same for every school; the school in the `/v1` path
 * only scopes who may read it. Both fail closed with a 500 when the registry is
 * empty, because an empty catalog would read as "no sync types exist".
 *
 * `syncType` stays `z.string()`: the backend registry is the list, and a
 * hand-kept enum here would be one more copy to drift.
 */

import { z } from 'zod'
import { SyncPrioritySchema, SyncWindowSchema } from '../../schemas/entities/sync'

export const SyncCatalogScheduleSchema = z.object({
  /** `autopilot`: the scheduled tick runs it; `on-demand`: only a trigger does; `dispatch-only`: only a parent job emits it. */
  mode: z.enum(['autopilot', 'on-demand', 'dispatch-only']),
  frequencyMinutes: z.number(),
  priority: SyncPrioritySchema,
  /** Names the window the sync is BLOCKED in, not the one it runs in. Render with `SYNC_WINDOW_LABELS`. */
  window: SyncWindowSchema,
  /** `isEnabled` on a freshly seeded per-school config row. */
  enabled: z.boolean(),
  /** The `sync_group` a seeded config row gets. */
  configGroup: z.string()
})
export type SyncCatalogSchedule = z.infer<typeof SyncCatalogScheduleSchema>

export const SyncCatalogEntrySchema = z.object({
  syncType: z.string(),
  label: z.string(),
  /** The step's dispatch group. */
  group: z.string(),
  credential: z.enum(['school', 'user', 'none']),
  /** Descriptor defaults. A `PLATFORM_SYNC_DEFAULTS` row, when one exists, overrides them fleet-wide. */
  schedule: SyncCatalogScheduleSchema,
  costCategory: z.string(),
  dependsOn: z.array(z.object({ syncType: z.string(), enforce: z.boolean() })),
  dedupKey: z.string().nullable(),
  /** Keys of the named sets this type belongs to. */
  sets: z.array(z.string()),
  /**
   * CHILD-ONLY types (SCH-3122): the parent types whose jobs create this one with
   * the per-item metadata it acts on. A job of the type without that metadata has
   * no target. Empty for a type that runs standalone.
   */
  childOf: z.array(z.string())
})
export type SyncCatalogEntry = z.infer<typeof SyncCatalogEntrySchema>

export const SyncCatalogNamedSetStepSchema = z.object({
  syncType: z.string(),
  label: z.string(),
  priority: SyncPrioritySchema,
  /** The sequence waits for this job before starting the next one. */
  waitForCompletion: z.boolean(),
  skipIfRecentlyCompleted: z.boolean(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  /** School setup only: the stage the step runs in. */
  stage: z.string().nullable(),
  executionMode: z.enum(['sync', 'async', 'queued']).nullable(),
  timeoutMs: z.number().nullable(),
  description: z.string().nullable()
})
export type SyncCatalogNamedSetStep = z.infer<typeof SyncCatalogNamedSetStepSchema>

export const SyncCatalogNamedSetSchema = z.object({
  /** The name `POST .../run-sequence` takes as `sequenceName`. */
  key: z.string(),
  displayName: z.string(),
  description: z.string(),
  /** `sequence` sets can be run by name; `school-setup` runs only inside the SCHOOL_SETUP_SEQUENCE job. */
  kind: z.enum(['sequence', 'school-setup']),
  steps: z.array(SyncCatalogNamedSetStepSchema)
})
export type SyncCatalogNamedSet = z.infer<typeof SyncCatalogNamedSetSchema>

export const GetSyncCatalogResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    syncTypes: z.array(SyncCatalogEntrySchema),
    namedSets: z.array(SyncCatalogNamedSetSchema)
  })
})
export type GetSyncCatalogResponse = z.infer<typeof GetSyncCatalogResponseSchema>
